import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { BookingDto, CancelBookingInput } from '@gc/shared';

import { api, ApiError } from '@/lib/api';
import { partyName } from './bookingLogic';

interface CancelBookingDialogProps {
  booking: BookingDto;
  onClose: () => void;
  onCancelled: () => void;
}

/** Cancel with an optional reason — the reason lands on the audit row. */
export function CancelBookingDialog({ booking, onClose, onCancelled }: CancelBookingDialogProps) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleCancel(): Promise<void> {
    setError(null);
    setSubmitting(true);
    try {
      const payload: CancelBookingInput = { reason: reason.trim() || null };
      await api.post(`/bookings/${booking.id}/cancel`, payload);
      onCancelled();
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t('bookings.errors.saveFailed'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/50"
        onClick={onClose}
        role="presentation"
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('bookings.cancelDialog.title')}
        className="fixed inset-0 z-50 grid place-items-center p-4"
      >
        <div className="w-full max-w-sm rounded-card border border-subtle bg-ground p-4 shadow-2xl">
          <h2 className="text-base font-semibold text-content">
            {t('bookings.cancelDialog.title')}
          </h2>
          <p className="mt-1 text-sm text-content-secondary">
            {t('bookings.cancelDialog.subtitle', {
              name: partyName(booking),
              device: booking.deviceName,
            })}
          </p>

          <label className="mt-3 block">
            <span className="mb-1 block text-xs text-content-secondary">
              {t('bookings.cancelDialog.reason')}
            </span>
            <textarea
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              maxLength={200}
              rows={2}
              className="w-full rounded-control border border-subtle bg-input px-3 py-2 text-sm text-content outline-none focus:border-primary"
            />
          </label>

          {error ? (
            <p role="alert" className="mt-2 text-sm text-danger">
              {error}
            </p>
          ) : null}

          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-full px-4 py-1.5 text-sm text-content-secondary hover:bg-hover hover:text-content"
            >
              {t('bookings.cancelDialog.keep')}
            </button>
            <button
              type="button"
              onClick={() => void handleCancel()}
              disabled={submitting}
              className="rounded-full bg-danger px-4 py-1.5 text-sm font-medium text-content-inverse transition-colors hover:bg-danger-hover disabled:opacity-60"
            >
              {t('bookings.cancelDialog.confirm')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
